// UI del bloque "Datos del profesional responsable" por turno (113 K / 114 M). Ver `profesionalPorTurnoDatos.ts` para la decisión de
// diseño: el turno que firma lo arma el servidor; los otros dos se teclean como texto estructurado.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { TURNOS_PROFESIONAL, type CampoProfesionalTurno, type EstadoProfesionalMultiple, type TurnoProfesional } from './profesionalPorTurnoDatos';

export interface ProfesionalPorTurnoProps<K extends string = TurnoProfesional> {
  /** Prefijo i18n (`formularios.f113.profesionalTurnos`): `titulo`, `nota`, `firmante.*`, `turno.*`, `campo.*`. */
  readonly prefijoI18n: string;
  /** Prefijo para los `id` del DOM (`f113-prof`). */
  readonly prefijoId: string;
  readonly campos: readonly CampoProfesionalTurno[];
  readonly estado: EstadoProfesionalMultiple<K>;
  readonly onChange: (v: EstadoProfesionalMultiple<K>) => void;
  /** Claves de los profesionales (`turno.<clave>` en i18n). Por defecto AM/PM/HS (113/114). */
  readonly turnos?: readonly K[];
}

const NINGUNO = 'ninguno';

export function ProfesionalPorTurno<K extends string = TurnoProfesional>({ prefijoI18n, prefijoId, campos, estado, onChange, turnos }: ProfesionalPorTurnoProps<K>): JSX.Element {
  const { t } = useTranslation();
  const lista = turnos ?? (TURNOS_PROFESIONAL as unknown as readonly K[]);
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${prefijoI18n}.titulo`)}</h4>
      <p className="text-sm text-hce-muted">{t(`${prefijoI18n}.nota`)}</p>
      <div className="flex flex-col gap-2">
        <Label>{t(`${prefijoI18n}.firmante.label`)}</Label>
        <RadioGroup
          value={estado.firmante || NINGUNO}
          onValueChange={(v) => onChange({ ...estado, firmante: v === NINGUNO ? '' : (v as K) })}
          className="flex flex-row flex-wrap gap-6"
        >
          {[NINGUNO, ...lista].map((valor) => (
            <div key={valor} className="flex items-center gap-2">
              <RadioGroupItem value={valor} id={`${prefijoId}-firmante-${valor}`} />
              <Label htmlFor={`${prefijoId}-firmante-${valor}`} className="font-normal">
                {valor === NINGUNO ? t(`${prefijoI18n}.firmante.ninguno`) : t(`${prefijoI18n}.turno.${valor}`)}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>
      {lista.map((turno) => (
        <div key={turno} className="flex flex-col gap-2 rounded-md border border-border p-3">
          <p className="text-sm font-medium text-hce-ink">{t(`${prefijoI18n}.turno.${turno}`)}</p>
          {estado.firmante === turno ? (
            <p className="text-sm text-hce-muted">{t(`${prefijoI18n}.firmante.armadoPorServidor`)}</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
              {campos.map((campo) => (
                <div key={campo} className="flex flex-col gap-1">
                  <Label htmlFor={`${prefijoId}-${turno}-${campo}`}>{t(`${prefijoI18n}.campo.${campo}`)}</Label>
                  <Input
                    id={`${prefijoId}-${turno}-${campo}`}
                    type={campo === 'fecha' ? 'date' : 'text'}
                    value={estado[turno]?.[campo] ?? ''}
                    onChange={(e) => onChange({ ...estado, [turno]: { ...estado[turno], [campo]: e.target.value } } as EstadoProfesionalMultiple<K>)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
